import type { TerminalShift, TerminalShiftSale } from '@erp/shared';
import { Printer, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDateTime } from '../../lib/intl.js';

import { tillBreakdown, tillDrawerCards, tillSaleRows } from './shiftBreakdown.js';
import { buildShiftPrintHtml, printHtml } from './shiftReport.js';
import {
  DrawerCards, NoteBox, PaymentMethods, SalesByCurrency, ShiftTiles,
  useDuration, useShiftMoney, useTodayRates,
} from './ShiftView.js';

/**
 * One TILL (.exe) close-out, opened from the list — drawn by ShiftView, the
 * same layout as a web-register shift (SessionSalesDialog).
 *
 * The numbers come from the row itself — they are what the till counted and
 * printed, and re-deriving them here would let the office publish a second
 * opinion about money someone already signed for. The SALES are fetched, because
 * only the server can answer "which sales were rung up in this shift": the till
 * stamps its own shift id on each sale at queue time, and no timestamp can stand
 * in for it (an offline queue delivers a whole afternoon in one second).
 */
export function TerminalShiftDialog({ shift, onClose, onDeleted }: {
  shift: TerminalShift | null;
  onClose: () => void;
  onDeleted: (id: string) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation('pos');
  const { t: tc } = useTranslation();
  const tenant = useAuthStore((s) => s.tenant);
  const canAdmin = usePermission('pos.terminal.admin');
  const money = useShiftMoney();
  const duration = useDuration();
  const [sales, setSales] = useState<{ items: TerminalShiftSale[]; linked: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const rates = useTodayRates(shift?.currencyCode ?? null);

  const id = shift?.id ?? null;
  useEffect(() => {
    if (!id) { setSales(null); return; }
    let on = true;
    setSales(null);
    void api()
      .get<{ items: TerminalShiftSale[]; linked: boolean }>(`/pos/terminal-shifts/${id}/sales`)
      // A failed fetch must not read as "this shift sold nothing" — `linked:false`
      // is the same "we can't tell you" the server sends for an older terminal.
      .then((r) => { if (on) setSales(r.data); })
      .catch(() => { if (on) setSales({ items: [], linked: false }); });
    return () => { on = false; };
  }, [id]);

  if (!shift) return <Dialog open={false} onOpenChange={onClose}><DialogContent /></Dialog>;

  const locale = i18n.language;
  const when = (iso: string): string => formatDateTime(iso, { locale });

  const cards = tillDrawerCards(shift);
  const breakdown = tillBreakdown(shift);
  const rows = sales === null ? null : tillSaleRows(sales.items, shift.currencyCode, t, money);

  const print = (): void => {
    printHtml(buildShiftPrintHtml({
      cashier: shift.openedByName,
      device: shift.deviceName,
      openedAt: shift.openedAt,
      closedAt: shift.closedAt,
      salesCount: shift.salesCount,
      shiftCurrency: shift.currencyCode,
      breakdown,
      drawers: cards,
      note: shift.note,
      rows,
    }, t, {
      storeName: tenant?.name ?? '',
      locale,
      rtl: i18n.dir() === 'rtl',
      rates,
    }));
  };

  const remove = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: t('shifts.deleteTitle', { defaultValue: 'Delete this close-out?' }),
      message: t('shifts.deleteMessage', {
        defaultValue: 'The slip is removed from this list. The sales themselves are invoices and payments in the books and are NOT affected.',
      }),
      tone: 'danger',
      confirmLabel: tc('actions.delete', { defaultValue: 'Delete' }),
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api().delete(`/pos/terminal-shifts/${shift.id}`);
      toast.success(t('shifts.deleted', { defaultValue: 'Close-out deleted' }));
      onDeleted(shift.id);
    } catch (e) {
      // The one refusal worth naming: a posted shift points at a treasury transfer
      // and/or a journal entry, and dropping it would strand them in the ledger.
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict'
        ? t('shifts.deletePosted', { defaultValue: 'This close-out is posted to the ledger — reverse its handover / variance entry first.' })
        : tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  const others = [
    Number(shift.cardTaken) > 0 && `${t('shifts.card', { defaultValue: 'Card' })} ${money(shift.cardTaken, shift.currencyCode)}`,
    Number(shift.transferTaken) > 0 && `${t('shifts.transfer', { defaultValue: 'Transfer' })} ${money(shift.transferTaken, shift.currencyCode)}`,
    Number(shift.creditTaken) > 0 && `${t('shifts.credit', { defaultValue: 'On account' })} ${money(shift.creditTaken, shift.currencyCode)}`,
  ].filter(Boolean).join(' · ');

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('shifts.reportTitle', { defaultValue: 'Register close-out' })}</DialogTitle>
          <DialogDescription>
            {shift.openedByName ?? t('shifts.cashier', { defaultValue: 'Cashier' })}
            {shift.deviceName ? ` · ${shift.deviceName}` : ''} · {when(shift.openedAt)} → {when(shift.closedAt)} · {duration(shift.openedAt, shift.closedAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[75vh] flex-col gap-4 overflow-auto pe-1">
          <ShiftTiles
            salesCount={shift.salesCount}
            takings={cards.map((c) => ({ currencyCode: c.currencyCode, amount: c.takings }))}
            method={{ main: money(shift.cashTaken, shift.currencyCode), hint: others || t('shifts.cashOnly', { defaultValue: 'all cash' }) }}
            cards={cards}
            shiftCurrency={shift.currencyCode}
            rates={rates}
            breakdown={breakdown}
          />
          <PaymentMethods breakdown={breakdown} />
          <DrawerCards cards={cards} />
          <NoteBox note={shift.note} />
          <SalesByCurrency
            rows={rows}
            shiftCurrency={shift.currencyCode}
            emptyText={sales?.linked === false
              // "This terminal doesn't report it" and "nothing sold" are different
              // facts, and only the second is about the shop's day.
              ? t('shifts.salesUnlinked', { defaultValue: 'This terminal didn’t report which sales belonged to the shift. Terminals updated after this release do — the till’s own count above still stands.' })
              : t('shifts.salesNone', { defaultValue: 'No sales were rung up in this shift.' })}
          />
        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          {canAdmin ? (
            <Button variant="ghost" onClick={() => void remove()} loading={busy} className="text-danger hover:text-danger">
              <Trash2 className="h-4 w-4" aria-hidden />{tc('actions.delete', { defaultValue: 'Delete' })}
            </Button>
          ) : <span />}
          <span className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>{tc('actions.close', { defaultValue: 'Close' })}</Button>
            <Button onClick={print}>
              <Printer className="h-4 w-4" aria-hidden />{tc('actions.print', { defaultValue: 'Print' })}
            </Button>
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
